import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toastUtils } from "../../../Utils/helper";
import Dialog from "../../../components/common/Dialog";
import { FormField } from "../../../components/common/Input/Input";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import { FormSelect } from "../../../components/common/Select/FormSelect";
import PhoneInputProps from "../../../components/PhoneInputProps/PhoneInputProps";
import images from "../../../images";
import {
  useGetUserInfoQuery,
  useUpdateUserInfoMutation,
} from "../../../services/apiSlice";
import Breadcrumb from "../ProfileCommon/Breadcrumb";
import Button from "../../../components/v6/Button";
import FooterContact from "../../../components/Footer/FooterContact";
// import { formatPhoneNumber } from "../../../Utils/helper";

const EditUser = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const updateModalRef = useRef();

  const [formData, setFormData] = useState({
    username: "",
    phone: "",
    age: "",
    gender: "",
    country_code: "",
    phoneNumber: "",
  });
  const locationOptions = [{ label: "Singapore (SGD)", value: "SGD" }];

  const defaultLocationOption = locationOptions[0];
  const {
    data: userInfo,
    isLoading: isLoadingProfile,
    refetch,
  } = useGetUserInfoQuery();
  const [updateUserInfo] = useUpdateUserInfoMutation();

  useEffect(() => {
    if (userInfo?.data) {
      const { username, country_code, phone, age, gender } = userInfo.data;

      const rawPhone =
        typeof phone === "string" ? phone.replace(/[^0-9]/g, "") : "";

      const phoneFirst8Digits = rawPhone.slice(0, 8);

      const formattedPhoneNumber =
        phoneFirst8Digits.length > 4
          ? `${phoneFirst8Digits.slice(0, 4)}-${phoneFirst8Digits.slice(4, 8)}`
          : phoneFirst8Digits;

      const formattedPhone =
        country_code && phone ? `${country_code}${phone}` : "";

      setFormData((prev) => ({
        ...prev,
        username: username || "",
        phone: formattedPhone,
        age: age || "",
        gender: gender || "male",
        phoneNumber: formattedPhoneNumber || "",
      }));
    }
  }, [userInfo]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // const handlePhoneChange = useCallback((value) => {
  //   setFormData((prev) => ({ ...prev, phone: value || "" }));
  // }, []);

  const handleGenderChange = useCallback((selectedOption) => {
    if (selectedOption) {
      if (selectedOption.value) {
        setFormData((prev) => ({
          ...prev,
          gender: selectedOption.value,
        }));
      } else if (typeof selectedOption === "string") {
        setFormData((prev) => ({
          ...prev,
          gender: selectedOption,
        }));
      } else if (selectedOption.target && selectedOption.target.value) {
        setFormData((prev) => ({
          ...prev,
          gender: selectedOption.target.value,
        }));
      }
    } else {
    }
  }, []);

  const parsePhoneNumber = (phone) => {
    if (!phone) return { countryCode: "", phoneNumber: "" };

    const parsedPhone = parsePhoneNumberFromString(phone);
    return parsedPhone
      ? {
          countryCode: `+${parsedPhone.countryCallingCode}`,
          phoneNumber: parsedPhone.nationalNumber,
        }
      : { countryCode: "", phoneNumber: "" };
  };

  const handleConfirmUpdate = useCallback(async () => {
    const { countryCode, phoneNumber } = parsePhoneNumber(formData.phone);

    const body = {
      username: formData.username,
      age: formData.age,
      country_code: countryCode,
      phone: phoneNumber,
      gender: formData.gender,
    };

    try {
      const response = await updateUserInfo(body).unwrap();
      if (
        response.message === "Operation successful" ||
        response.status === 200
      ) {
        toastUtils.success(t("toast.success.updateSuccess"));
        await refetch();
        updateModalRef?.current?.close();
        navigate("/profile", {
          state: { direction: -1 },
          replace: false,
        });
      }
    } catch (error) {
      toastUtils.error(t("toast.error.updateFailed"));
    }
  }, [formData, updateUserInfo, navigate, t, refetch]);

  const handleUpdate = useCallback(() => {
    updateModalRef?.current?.open();
  }, []);

  const genderOptions = [
    { value: "male", label: t("signUp.gender.male") },
    { value: "female", label: t("signUp.gender.female") },
    { value: "maleIdentifying", label: t("signUp.gender.maleIdentifying") },
    {
      value: "femaleIdentifying",
      label: t("signUp.gender.femaleIdentifying"),
    },
    { value: "nonBinary", label: t("signUp.gender.nonBinary") },
    { value: "agender", label: t("signUp.gender.agender") },
    { value: "preferNotToSay", label: t("signUp.gender.preferNotToSay") },
  ];

  return (
    <div className="flex flex-col w-full max-w-[480px]">
      <LoadingCommon statusLoading={isLoadingProfile} />
      <div className="flex flex-col flex-1 gap-6 px-[24px] pb-[120px]">
        <Breadcrumb
          breadCrumbTiltle={t("editUser.title")}
          onClickBack={() =>
            navigate("/profile", {
              state: { direction: -1 },
              replace: false,
            })
          }
        />
        <form className="flex flex-col gap-6 w-full text-sm leading-none text-black">
          <FormField
            iconName="star"
            placeholder={t("editUser.username")}
            name="username"
            value={formData.username}
            onChange={handleChange}
          />
          <PhoneInputProps
            value={formData.phoneNumber}
            readOnly={true}
            className=" min-h-[50px]"
            // onChange={handlePhoneChange}
          />
          <FormField
            iconName="calendar"
            placeholder={t("editUser.age")}
            name="age"
            type="number"
            value={formData.age}
            maxLength={3}
            onChange={handleChange}
          />

          <FormSelect
            iconName="user"
            value={genderOptions.find(
              (option) =>
                option.value.toLowerCase() === formData.gender.toLowerCase(),
            )}
            onChange={handleGenderChange}
            options={genderOptions}
            name="gender"
          />

          <FormSelect
            iconName="location"
            value={defaultLocationOption}
            onChange={() => {}}
            options={locationOptions}
            name="subject"
            isDisabled={true}
          />
        </form>

        <div className="flex flex-col gap-3.5  w-full mb-6 items-center">
          <button
            onClick={handleUpdate}
            className="flex-1 shrink gap-2.5 self-stretch px-5 mt-4  text-xl font-medium text-white whitespace-nowrap bg-[#253FF6] rounded-xl min-h-[50px]"
          >
            {t("editUser.saveProfile")}
          </button>
        </div>
      </div>

      <Dialog ref={updateModalRef} hiddenCloseButton>
        <div className="flex flex-col gap-6 justify-center items-center">
          <p className="text-center">
            Are you sure you want to update your profile information?
          </p>
          <img src={images.UpdateModalImage} alt="" className="w-[80px]" />
          <div className="flex gap-2 w-full">
            <Button
              className="text-lg rounded-xl h-[50px] bg-white border border-mid-gray text-mid-gray flex-1"
              handleClick={() => updateModalRef?.current?.close()}
            >
              {t("base.cancel")}
            </Button>
            <Button
              className="text-lg rounded-xl h-[50px] bg-primary border border-primary text-white flex-1"
              handleClick={handleConfirmUpdate}
            >
              {t("base.update")}
            </Button>
          </div>
        </div>
      </Dialog>
      <FooterContact className={"!bg-white"} />
    </div>
  );
};

export default EditUser;
